import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { SortableTable, type Column } from '@/components/SortableTable'
import { StatTile } from '@/components/StatTile'
import { INPUT } from '@/features/inventory/shared'
import { fmtDate, fmtEur, fmtNumber, intlLocale } from '@/i18n'
import { STATES, type Portfolio, type PortfolioRow, type State } from './types'
import { kindLabel } from './ExecutionPanel'
import { STATE_BG, STRIPES, StateChip, reasonLabel, stateLabel, useExecT, type ExecT } from './shared'

const eurShort = (n: number) => new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'EUR', notation: 'compact', maximumFractionDigits: 1 }).format(n)

const FLOW: State[] = ['offer', 'negotiation', 'contracted', 'ready', 'in_progress', 'completed', 'accepted', 'closed']

/**
 * Project execution for the CEO (docs/project-execution-design.md): where every project is in its
 * lifecycle, how long each state takes, and per project what it built and earned.
 */
export function PortfolioPage() {
  const t = useExecT()
  const { data, isPending } = useQuery<Portfolio>({ queryKey: ['portfolio'], queryFn: () => api.get<Portfolio>('/api/execution/portfolio') })
  const [q, setQ] = useState('')
  const [state, setState] = useState<State | ''>('')
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase()
    return (data?.rows ?? []).filter((r) => (!state || r.state === state) &&
      (!n || [r.name, r.code, r.provider].some((x) => x?.toLowerCase().includes(n))))
  }, [data, q, state])

  const loader = usePageLoader(isPending, t('loading'))
  if (loader) return loader
  if (!data) return null
  const all = data.rows

  const columns: Column<PortfolioRow>[] = [
    { key: 'name', header: t('colProject'), sortValue: (r) => r.name ?? '', render: (r) => (
      <Link to={`/projects/${r.projectId}`} className="font-medium hover:underline">{r.name ?? '—'}
        <div className="text-xs font-normal text-neutral-500">{[r.code, r.provider].filter(Boolean).join(' · ')}</div></Link>) },
    { key: 'state', header: t('colState'), sortValue: (r) => STATES.indexOf(r.state ?? 'offer'), render: (r) => <><StateChip state={r.state} derived={r.derived} />{r.idleDays != null && <span className="mt-0.5 block text-[11px] text-neutral-500">{t('idle', { n: r.idleDays })}</span>}</> },
    { key: 'days', header: t('colDays'), align: 'right', sortValue: (r) => r.daysInState, render: (r) => <span className="tabular">{r.daysInState}</span> },
    { key: 'blocked', header: t('colBlocked'), sortValue: (r) => (r.blockedReason ? 100_000 : 0) + r.blockedDays, render: (r) => r.blockedReason
      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-300"><span className="inline-block h-2.5 w-4 rounded-sm" style={STRIPES} />{reasonLabel(t, r.blockedReason)}</span>
      : r.blockedDays ? <span className="tabular text-xs text-neutral-500">{t('days', { n: r.blockedDays })}</span> : '' },
    { key: 'end', header: t('colEnd'), sortValue: (r) => r.daysLate ?? -1, render: (r) => r.plannedEnd ? (
      <span className="tabular text-xs">{fmtDate(r.plannedEnd)}{r.daysLate ? <span className="block font-semibold text-red-700 dark:text-red-300">{t('daysLate', { n: r.daysLate })}</span> : null}</span>) : '—' },
    { key: 'invoiced', header: t('colInvoiced'), align: 'right', sortValue: (r) => r.invoiced, render: (r) => (
      <span className="tabular">{fmtEur(r.invoiced)}{r.contractValue ? <span className="block text-xs text-neutral-500">{Math.round((r.invoiced / r.contractValue) * 100)} %</span> : null}</span>) },
    { key: 'paid', header: t('colPaid'), align: 'right', sortValue: (r) => r.paid, render: (r) => <span className="tabular">{fmtEur(r.paid)}</span> },
    { key: 'trench', header: t('colTrench'), align: 'right', sortValue: (r) => r.trenchM, render: (r) => <span className="tabular">{fmtNumber(r.trenchM)}</span> },
    { key: 'ha', header: t('colHa'), align: 'right', sortValue: (r) => r.ha, render: (r) => <span className="tabular">{fmtNumber(r.ha)}</span> },
    { key: 'hpPlus', header: t('colHpPlus'), align: 'right', sortValue: (r) => r.hpPlus, render: (r) => <span className="tabular">{fmtNumber(r.hpPlus)}</span> },
    { key: 'last', header: t('colLastWorked'), sortValue: (r) => r.lastWorked ?? '', render: (r) => <span className="tabular text-xs">{fmtDate(r.lastWorked)}</span> },
  ]

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('portfolioTitle')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-neutral-500">{t('portfolioSubtitle')}</p>
      </header>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3 lg:grid-cols-7">
        <StatTile label={t('tileProjects')} value={all.length} unit="" />
        <StatTile label={t('tileInProgress')} value={all.filter((r) => r.state === 'in_progress' && r.idleDays == null).length} unit="" />
        <StatTile label={t('tileIdle')} value={all.filter((r) => r.idleDays != null).length} unit="" />
        <StatTile label={t('tileBlocked')} value={all.filter((r) => r.blockedReason).length} unit="" />
        <StatTile label={t('tileLate')} value={all.filter((r) => (r.daysLate ?? 0) > 0).length} unit="" />
        <StatTile label={t('tileInvoiced')} value={data.invoiced} unit="EUR" display={eurShort(data.invoiced)} />
        <StatTile label={t('tileOwed')} value={data.owed} unit="EUR" display={eurShort(data.owed)} />
      </div>
      {data.paidAsOf && <p className="-mt-3 text-xs text-neutral-500">{t('paidAsOf', { date: fmtDate(data.paidAsOf) })}</p>}

      <Built t={t} data={data} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Funnel t={t} data={data} active={state} onPick={(s) => setState(state === s ? '' : s)} />
        <TimeInState t={t} data={data} />
      </div>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} aria-label={t('search')} className={`${INPUT} basis-full sm:basis-80`} />
          {state && <button type="button" onClick={() => setState('')} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1.5 text-xs dark:bg-neutral-800"><StateChip state={state} /> ✕</button>}
          <span className="ml-auto text-xs text-neutral-500">{t('shownOf', { shown: rows.length, total: all.length })}</span>
        </div>
        <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
          <SortableTable columns={columns} rows={rows} rowKey={(r) => r.projectId} initialSort={{ key: 'state', dir: 'asc' }} />
        </div>
        <p className="text-xs text-neutral-500">{t('derivedLegend')}</p>
      </section>
    </div>
  )
}

/** Projects per state along the lifecycle, with contract value; exits (lost, cancelled) at the end. */
function Funnel({ t, data, active, onPick }: { t: ExecT; data: Portfolio; active: State | ''; onPick: (s: State) => void }) {
  const steps = data.funnel.filter((f) => FLOW.includes(f.state) || f.projects > 0)
  const max = Math.max(1, ...steps.map((s) => s.projects))
  return (
    <section className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h2 className="mb-3 text-sm font-semibold">{t('funnelTitle')}</h2>
      <ul className="space-y-1.5">
        {steps.map((s) => (
          <li key={s.state}>
            <button type="button" onClick={() => onPick(s.state)} className={`grid w-full grid-cols-[7.5rem_minmax(0,1fr)_7rem] items-center gap-2 rounded-lg px-1 py-0.5 text-left text-sm ${active === s.state ? 'bg-neutral-100 dark:bg-neutral-800' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'}`}>
              <span className="truncate text-xs font-medium">{stateLabel(t, s.state)}</span>
              <span className="relative h-5 rounded bg-neutral-100 dark:bg-neutral-800">
                <span className={`absolute inset-y-0 left-0 rounded ${STATE_BG[s.state]}`} style={{ width: `${(s.projects / max) * 100}%` }} />
                {s.idle > 0 && <span className="absolute inset-y-0 rounded-r bg-neutral-300 dark:bg-neutral-600" style={{ left: `${((s.projects - s.idle) / max) * 100}%`, width: `${(s.idle / max) * 100}%` }} title={t('funnelIdle', { n: s.idle })} />}
                {s.blocked > 0 && <span className="absolute inset-y-0 left-0 rounded" style={{ ...STRIPES, width: `${(s.blocked / max) * 100}%` }} />}
                <span className="tabular absolute inset-y-0 left-1.5 flex items-center text-[11px] font-semibold text-white mix-blend-difference">{s.projects}</span>
              </span>
              <span className="tabular text-right text-xs text-neutral-500">{s.contractValue ? fmtEur(s.contractValue) : ''}{s.idle > 0 && <span className="block">{t('funnelIdle', { n: s.idle })}</span>}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Median (bar) and 75th percentile (tick) of finished stays per state; ongoing stays separately. */
function TimeInState({ t, data }: { t: ExecT; data: Portfolio }) {
  const rows = data.timeInState.filter((s) => s.finished > 0 || s.ongoing > 0)
  const max = Math.max(1, ...rows.map((s) => Math.max(s.p75Days ?? 0, s.ongoingMedianDays ?? 0)))
  return (
    <section className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h2 className="text-sm font-semibold">{t('timeTitle')}</h2>
      <p className="mb-3 mt-0.5 text-xs text-neutral-500">{t('timeHint')}</p>
      <ul className="space-y-2.5">
        {rows.map((s) => (
          <li key={s.state} className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-start gap-2 text-sm">
            <span className="truncate pt-0.5 text-xs font-medium">{stateLabel(t, s.state)}</span>
            <div className="space-y-0.5">
              {s.medianDays != null ? (
                <div className="flex items-center gap-2">
                  <span className="relative h-4 flex-1 rounded bg-neutral-100 dark:bg-neutral-800">
                    <span className={`absolute inset-y-0 left-0 rounded ${STATE_BG[s.state]}`} style={{ width: `${(s.medianDays / max) * 100}%` }} />
                    {s.p75Days != null && <span className="absolute -inset-y-0.5 w-0.5 bg-neutral-700 dark:bg-neutral-200" style={{ left: `${(s.p75Days / max) * 100}%` }} />}
                  </span>
                  <span className="tabular w-28 shrink-0 text-right text-xs">{t('days', { n: Math.round(s.medianDays) })}{s.medianNetDays != null && s.medianNetDays !== s.medianDays ? ` (${Math.round(s.medianNetDays)})` : ''}</span>
                </div>
              ) : <span className="text-xs text-neutral-400">{t('timeNone')}</span>}
              {s.ongoing > 0 && <div className="text-[11px] text-neutral-500">{t('timeOngoing', { n: s.ongoing, d: Math.round(s.ongoingMedianDays ?? 0) })}</div>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** What all these projects built: connections and trench by surface (from the daily Aufmaß reports). */
function Built({ t, data }: { t: ExecT; data: Portfolio }) {
  const q = (k: string) => data.output.filter((o) => o.kind === k).reduce((s, o) => s + (o.quantity ?? 0), 0)
  const trench = data.output.filter((o) => o.kind === 'trench')
  const total = trench.reduce((s, o) => s + (o.quantity ?? 0), 0)
  return (
    <section className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h2 className="mb-3 text-sm font-semibold">{t('builtTitle')}</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {(['ha', 'hp_plus', 'hp', 'nha', 'pnha', 'dp'] as const).map((k) => (
          <div key={k}>
            <div className="text-xs text-neutral-500">{kindLabel(t, { kind: k, surface: null, quantity: null, planned: null })}</div>
            <div className="tabular text-xl font-semibold">{fmtNumber(q(k))}</div>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <div className="mb-1 flex justify-between text-xs text-neutral-500"><span>{t('kindTrench_total').replace(' (m)', '')}</span><span className="tabular font-semibold text-neutral-800 dark:text-neutral-100">{fmtNumber(total / 1000, 1)} km</span></div>
        <div className="flex h-5 overflow-hidden rounded bg-neutral-100 dark:bg-neutral-800">
          {trench.map((o, i) => (
            <span key={o.surface} title={`${kindLabel(t, o)}: ${fmtNumber(o.quantity ?? 0)} m`} className="h-full"
              style={{ width: `${((o.quantity ?? 0) / Math.max(1, total)) * 100}%`, background: TRENCH_COLORS[i % TRENCH_COLORS.length] }} />
          ))}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300">
          {trench.map((o, i) => (
            <span key={o.surface} className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm" style={{ background: TRENCH_COLORS[i % TRENCH_COLORS.length] }} />
              {kindLabel(t, o).replace(/^.*· /, '')} <span className="tabular text-neutral-500">{fmtNumber((o.quantity ?? 0) / 1000, 1)} km</span></span>
          ))}
        </div>
      </div>
    </section>
  )
}

const TRENCH_COLORS = ['#475569', '#b45309', '#0e7490', '#65a30d', '#9333ea', '#be123c', '#a16207', '#64748b']
